import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, clearToken, getToken, setToken } from '../api';
import { applyTeamClass } from '../theme/teamPalette';

export type User = {
  id: string;
  username: string;
  displayName: string;
  favoriteTeam: string | null;
  isAdmin: boolean;
};

type AuthState = {
  user: User | null;
  loading: boolean;
  login(token: string, user: User): void;
  logout(): void;
  refreshMe(): Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  async function refreshMe() {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await api<User>('/api/auth/me');
      setUser(me);
      applyTeamClass(me.favoriteTeam);
    } catch {
      setUser(null);
      clearToken();
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refreshMe();
    function onUnauth() {
      setUser(null);
      applyTeamClass(null);
    }
    window.addEventListener('doppelklack:unauthorized', onUnauth);
    return () => window.removeEventListener('doppelklack:unauthorized', onUnauth);
  }, []);

  const value: AuthState = {
    user,
    loading,
    login(token, user) {
      setToken(token);
      setUser(user);
      applyTeamClass(user.favoriteTeam);
    },
    logout() {
      clearToken();
      setUser(null);
      applyTeamClass(null);
    },
    refreshMe,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss innerhalb von <AuthProvider> stehen');
  return ctx;
}
